import { useEffect } from "react";

/**
 * Injects/updates <title> and Open Graph meta tags in <head>.
 * Pure DOM manipulation — no react-helmet dependency.
 *
 * Priority for each field:
 *   1. `seo.<field>` from CMS (site_pages.seo_*)
 *   2. Fallback prop passed by the page
 *   3. Site-wide defaults from site_settings (loaded once)
 *
 * Usage:
 *   <PageSEO
 *     pageKey="home"
 *     seo={content?.seo}
 *     defaults={{ title: "CCN Sanluri", description: "..." }}
 *   />
 */
export function PageSEO({ seo, defaults = {}, siteSettings = null }) {
  useEffect(() => {
    const title = (seo?.title && seo.title.trim()) || defaults.title || siteSettings?.seo_title || siteSettings?.site_name || "CCN Sanluri";
    const description = (seo?.description && seo.description.trim()) || defaults.description || siteSettings?.seo_description || "Il Centro Commerciale Naturale di Sanluri: attività, eventi, storia del centro.";
    const ogImage = (seo?.og_image && seo.og_image.trim()) || defaults.ogImage || siteSettings?.seo_og_image || null;
    // Canonical: prefer explicit CMS/DB value; otherwise use the current page
    // URL WITHOUT query string / hash so tracking params (?utm_source=…) do
    // not create duplicate canonicals — this is the recommended SEO behavior.
    const canonical = (seo?.canonical_url && seo.canonical_url.trim())
      || (typeof window !== "undefined" ? window.location.origin + window.location.pathname : "");
    const noindex = !!seo?.noindex;
    const nofollow = !!seo?.nofollow;
    const keywords = (seo?.keywords && seo.keywords.trim()) || defaults.keywords || null;

    document.title = title;

    upsertMeta("description", description, { attr: "name" });
    if (keywords) upsertMeta("keywords", keywords, { attr: "name" });

    // Robots
    const robotsParts = [];
    robotsParts.push(noindex ? "noindex" : "index");
    robotsParts.push(nofollow ? "nofollow" : "follow");
    upsertMeta("robots", robotsParts.join(","), { attr: "name" });

    // Open Graph
    upsertMeta("og:title", title, { attr: "property" });
    upsertMeta("og:description", description, { attr: "property" });
    upsertMeta("og:type", "website", { attr: "property" });
    upsertMeta("og:url", canonical, { attr: "property" });
    upsertMeta("og:locale", "it_IT", { attr: "property" });
    upsertMeta("og:site_name", siteSettings?.site_name || "CCN Sanluri", { attr: "property" });
    if (ogImage) upsertMeta("og:image", ogImage, { attr: "property" });

    // Twitter Card
    upsertMeta("twitter:card", ogImage ? "summary_large_image" : "summary", { attr: "name" });
    upsertMeta("twitter:title", title, { attr: "name" });
    upsertMeta("twitter:description", description, { attr: "name" });
    if (ogImage) upsertMeta("twitter:image", ogImage, { attr: "name" });

    // Canonical link
    upsertLink("canonical", canonical);
  }, [seo, defaults, siteSettings]);

  return null;
}

function upsertMeta(key, value, { attr }) {
  if (!value) return;
  let el = document.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", String(value));
}

function upsertLink(rel, href) {
  if (!href) return;
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}
